import React, { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import NumberUtils from '../../utils/numberUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useAuth from '../../providers/Auth/Auth.provider';

interface ITwNormalCamera {
  open: boolean;
  onFileUpload: (files: File[]) => void;
  close: () => void;
}
const baseStyle = {
  flex: 1,
  display: 'flex',
  height: '60px',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
  borderWidth: 1,
  borderRadius: 4,
  borderColor: '#FF6700',
  borderStyle: 'dashed',
  backgroundColor: '#F2F1F9',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  fontSize: '14px',
  opacity: '0',
  cursor: 'pointer'
};

const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15 MB
const TwNormalCamera: React.FC<ITwNormalCamera> = ({ open, onFileUpload, close }): JSX.Element => {
  const {
    data: { userData }
  } = useAuth();
  const { addNotification } = useNotifications();

  const onDropAccepted = (acceptedFiles: File[]): void => {
    onFileUpload(acceptedFiles);
  };

  const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
    if (fileRejections.length > 0) {
      const error = fileRejections[0].errors[0];
      if (error.code === 'file-too-large') {
        logRocketTrack('Upload document', {
          action: 'Upload file, file(s) too large',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: `Error: file size on ${
            fileRejections[0].file.name
          } is greater than ${NumberUtils.getFileSize(MAX_FILE_SIZE || 0)}`,
          variant: 'error'
        });
      }
      if (error.code === 'file-invalid-type') {
        logRocketTrack('Upload document', {
          action: 'Upload file, invalid file type',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: `Error: Invalid file type, importer only accepts images`,
          variant: 'error'
        });
      }
      if (error.code === 'too-many-files') {
        logRocketTrack('Upload document', {
          action: 'Upload multiple files',
          severity: 'low',
          tenant_id: userData?.tenant?.id,
          company_name: userData?.tenant?.company_name,
          email: userData?.email_address,
          user_first_name: userData?.first_name,
          user_last_name: userData?.last_name
        });
        addNotification({
          message: `Error: Too many files, max amount is 10`,
          variant: 'error'
        });
      }
    }

    close();
  }, []);

  const { getRootProps, getInputProps, inputRef } = useDropzone({
    disabled: false,
    accept: ['image/heic', 'image/jpeg', 'image/png', 'application/pdf'],
    multiple: true,
    maxFiles: 10,
    maxSize: MAX_FILE_SIZE,

    onDropRejected,
    onDropAccepted
  });

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.click();

      inputRef.current.addEventListener('cancel', (event) => {
        close();
      });
    }
  }, [open, inputRef]);

  const style = useMemo(
    () => ({
      ...baseStyle
    }),
    []
  );

  return (
    <div {...getRootProps({ style })}>
      <input {...getInputProps()} />
    </div>
  );
};

export default TwNormalCamera;
